import { Card } from '@aws-amplify/ui-react';
import { useContext, useEffect } from 'react';
import { TbHeadset } from 'react-icons/tb';
import { Navigate } from 'react-router-dom';
import { GlobalContext } from '../global-context';
import General from '../utils/GeneralUtils';
import './Support.scss';

/**
 * The HubSpot support form, wired to the Customer Support Tickets pipeline: a submission
 * opens a ticket straight away. The embed script renders every `.hs-form-frame` on the page
 * and watches the DOM for new ones, so loading it once is enough for every later visit.
 */
const HUBSPOT_PORTAL_ID = '23973422';
const HUBSPOT_REGION = 'na1';
const HUBSPOT_FORM_ID = 'c948b4be-b167-4a2e-9660-47aceeb818b3';
const HUBSPOT_EMBED_SCRIPT = `https://js.hsforms.net/forms/embed/${HUBSPOT_PORTAL_ID}.js`;

/** Appends the HubSpot forms embed script once; later calls find it already there. */
function loadHubSpotForms(): void {
  if (document.querySelector(`script[src="${HUBSPOT_EMBED_SCRIPT}"]`)) return;
  const script = document.createElement('script');
  script.src = HUBSPOT_EMBED_SCRIPT;
  script.defer = true;
  document.head.appendChild(script);
}

/**
 * Support, for paying teams: the HubSpot form, which files a ticket with our team directly.
 * Trial teams are sent home.
 */
export function Support() {
  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'Support', globalContext);

  const authInfo = globalContext.authInfo;
  const isPaid = !authInfo || General.isPaidTier(authInfo.team?.tier);

  useEffect(() => {
    if (isPaid) loadHubSpotForms();
  }, [isPaid]);

  if (!isPaid) return <Navigate to="/" replace />;

  return (
    <div className="content content-1000 support">
      <Card variation="outlined" className="support__card">
        <div className="support__icon">
          <TbHeadset />
        </div>
        <h2 className="support__title">How can we help?</h2>
        <p className="support__lead">
          Questions, problems or ideas: tell us below. Your message opens a ticket with our support
          team, and we reply by email.
        </p>
        <div
          className="hs-form-frame support__form"
          data-region={HUBSPOT_REGION}
          data-form-id={HUBSPOT_FORM_ID}
          data-portal-id={HUBSPOT_PORTAL_ID}
        />
      </Card>
    </div>
  );
}
